// Timeout only the first attempt; the single retry may wait for a slow server.
// Both attempts still respect the caller cancellation signal.
export function createApiFetch(fetch, config) {
	return async (url, options = {}) => {
		const policy = config.fetchApi?.safeRequests || {};
		if (policy.enabled === false) return fetch(url, options);
		const method = String(options.method || 'GET').toUpperCase();
		const path = String(url).split(/[?#]/)[0].replace(/\/$/, '');
		const matches = entry => entry.method === method &&
			String(entry.endpoint ? config.hapiEndpoints?.[entry.endpoint] || '' : entry.path || '').replace(/\/$/, '') === path;
		const safe = (policy.endpoints || []).some(matches);
		// Unlisted operations never inherit ofetch's implicit GET retry.
		if (!safe) return fetch(url, {...options, retry: false});
		const timeout = options.timeout ?? policy.timeout ?? 10000;
		const retry = options.retry !== false && options.retry !== 0;
		const delay = policy.retryDelay ?? 500;
		const signal = options.signal;
		for (let attempt = 0; ; attempt++) {
			signal?.throwIfAborted();
			const controller = new AbortController();
			const cancel = () => controller.abort(signal.reason);
			signal?.addEventListener('abort', cancel, {once: true});
			let timedOut = false;
			const timer = attempt === 0 && timeout > 0 ? setTimeout(() => {
				timedOut = true;
				controller.abort(new DOMException('Request timed out', 'TimeoutError'));
			}, timeout) : undefined;
			try {
				return await fetch(url, {...options, timeout: 0, retry: false, signal: controller.signal});
			} catch (error) {
				const willRetry = retry && attempt === 0 && !signal?.aborted && timedOut;
				if (!willRetry) throw error;
			} finally {
				clearTimeout(timer);
				signal?.removeEventListener('abort', cancel);
			}
			// Cancellation during the delay must also prevent the next attempt.
			await new Promise((resolve, reject) => {
				const cancelDelay = () => {
					clearTimeout(timer);
					signal.removeEventListener('abort', cancelDelay);
					reject(signal.reason);
				};
				const timer = setTimeout(() => {
					signal?.removeEventListener('abort', cancelDelay);
					resolve();
				}, delay);
				signal?.addEventListener('abort', cancelDelay, {once: true});
				if (signal?.aborted) cancelDelay();
			});
		}
	};
}
